import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { MatDialog, MatDialogRef } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { fuseAnimations } from "@fuse/animations";
import { FuseTranslationLoaderService } from "@fuse/services/translation-loader.service";
import { ProjectModel, ProjectPeriodModel } from "app/models/project.model";
import { getProjectPeriodSegments, normalizeProjectPeriod, validateProjectPeriods } from "app/shared/project-periods.core";
import {
    rebalanceEditedProjectPeriodCoverage,
    removeProjectPeriodCoverage,
    replaceProjectPeriodCoverage,
} from "app/shared/project-periods.editor";
import { ProjectService } from "app/services/api/project.service";
import { ToastrService } from "ngx-toastr";
import { locale as catalan } from "../../i18n/ca";
import { locale as english } from "../../i18n/en";
import { locale as spanish } from "../../i18n/es";
import { ProjectPeriodAddEditComponent } from "../project-period-add-edit/project-period-add-edit.component";

type TimelineSegment = {
    key: string;
    period: ProjectPeriodModel;
    segmentStart: number;
    segmentEnd: number;
    left: number;
    width: number;
    canSplit: boolean;
    toneIndex: number;
    isVirtual: boolean;
};

type HoveredSplit = {
    left: number;
    label: string;
};

@Component({
    selector: "app-project-periods",
    templateUrl: "./project-periods.component.html",
    styleUrls: ["./project-periods.component.scss"],
    animations: fuseAnimations,
    standalone: false,
})
export class ProjectPeriodsComponent implements OnChanges {
    @Input()
    project: ProjectModel = { periods: [] } as ProjectModel;

    private dialogRef: MatDialogRef<ProjectPeriodAddEditComponent>;
    savedChangesString: string;
    hoveredSplit: HoveredSplit | null = null;

    readonly hourMarkers = Array.from({ length: 24 }, (_, hour) => hour);

    constructor(
        private translation: FuseTranslationLoaderService,
        private projectService: ProjectService,
        private matDialog: MatDialog,
        private toastr: ToastrService,
        private router: Router,
    ) {
        this.translation.loadTranslations(english, spanish, catalan);

        translation.translateService.get("GLOBAL", { value: "SavedChanges" }).subscribe((res) => {
            this.savedChangesString = res.SavedChanges;
        });
    }

    ngOnChanges(_changes: SimpleChanges) {
        this.project.periods = this.getNormalizedPeriods(this.project.periods ?? []);
    }

    get hasPeriods(): boolean {
        return !!this.project?.periods?.length;
    }

    get displayPeriods(): ProjectPeriodModel[] {
        return this.hasPeriods ? this.project.periods : [this.buildDefaultDayPeriod()];
    }

    get validationMessage(): string | null {
        if (!this.hasPeriods) {
            return null;
        }

        return validateProjectPeriods(this.project?.periods ?? []);
    }

    get invalidPeriods(): ProjectPeriodModel[] {
        return (this.project.periods ?? []).filter((period) => getProjectPeriodSegments(period).length === 0);
    }

    get listedPeriods(): ProjectPeriodModel[] {
        return this.project.periods ?? [];
    }

    get hasTimelineSegments(): boolean {
        return this.timelineSegments.length > 0;
    }

    get timelineSegments(): TimelineSegment[] {
        return this.displayPeriods
            .flatMap((period, index) => this.toTimelineSegments(period, index, !this.hasPeriods))
            .sort((left, right) => left.segmentStart - right.segmentStart);
    }

    addPeriod() {
        this.dialogRef = this.openPeriodPanel({
            periods: this.project.periods ?? [],
        });
        this.dialogRef.afterClosed().subscribe((response) => {
            if (!response?.period) {
                return;
            }

            this.persistPeriods(this.getUpsertedPeriods(response.period));
        });
    }

    editPeriod(period: ProjectPeriodModel, event?: MouseEvent) {
        event?.stopPropagation();
        this.dialogRef = this.openPeriodPanel({
            period,
            periods: this.project.periods ?? [],
        });
        this.dialogRef.afterClosed().subscribe((response) => {
            if (!response?.period) {
                return;
            }

            this.persistPeriods(rebalanceEditedProjectPeriodCoverage(this.project.periods ?? [], period.name, response.period));
        });
    }

    deletePeriod(period: ProjectPeriodModel, event?: MouseEvent) {
        event?.stopPropagation();
        this.persistPeriods(removeProjectPeriodCoverage(this.project.periods ?? [], period.name));
    }

    formatSegmentTime(segment: TimelineSegment): string {
        return `${this.formatMinute(segment.segmentStart)} - ${this.formatSegmentEnd(segment)}`;
    }

    getDisplayIntervals(period: ProjectPeriodModel): string[] {
        const segments = getProjectPeriodSegments(period);
        if (!segments.length) {
            return ["No intervals"];
        }

        return segments.map((segment) => `${this.formatMinute(segment.start)} - ${this.formatSegmentBoundary(segment.start, segment.end)}`);
    }

    onSegmentClick(segment: TimelineSegment, event: MouseEvent) {
        this.splitPeriod(segment, event);
    }

    onTimelineHover(event: MouseEvent) {
        const timelineMinute = this.getTimelineMinute(event);
        if (timelineMinute === null) {
            this.hoveredSplit = null;
            return;
        }

        const segment = this.timelineSegments.find(
            (candidate) => candidate.canSplit && timelineMinute >= candidate.segmentStart && timelineMinute < candidate.segmentEnd,
        );
        if (!segment) {
            this.hoveredSplit = null;
            return;
        }

        const splitAt = this.clampSplitMinute(segment, timelineMinute);
        if (splitAt === null) {
            this.hoveredSplit = null;
            return;
        }

        this.hoveredSplit = {
            left: (splitAt / (24 * 60)) * 100,
            label: this.formatMinute(splitAt),
        };
    }

    clearHoveredSplit() {
        this.hoveredSplit = null;
    }

    formatHour(hour: number): string {
        return `${hour.toString().padStart(2, "0")}:00`;
    }

    private persistPeriods(periods: ProjectPeriodModel[]) {
        const previousPeriods = this.getNormalizedPeriods(this.project.periods ?? []);
        const nextPeriods = this.getNormalizedPeriods(periods ?? []);

        this.project = {
            ...this.project,
            periods: nextPeriods,
        };

        this.projectService.edit(this.project).subscribe(
            (data) => {
                this.project = {
                    ...data,
                    periods: this.getNormalizedPeriods(data.periods ?? []),
                };
                this.toastr.success(this.savedChangesString);
            },
            (error) => {
                console.log(error);
                this.project = {
                    ...this.project,
                    periods: previousPeriods,
                };
                this.toastr.error("Error");
                if (error.error.statusCode === 404) {
                    this.router.navigate(["/admin/projects"]);
                }
            },
        );
    }

    private splitPeriod(segment: TimelineSegment, event: MouseEvent) {
        event?.stopPropagation();

        const splitAt = this.getSplitMinute(segment, event);
        if (splitAt === null) {
            return;
        }

        this.dialogRef = this.openPeriodPanel({
            periods: this.project.periods ?? [],
            initialPeriod: this.buildSplitPeriod(segment, splitAt),
        });

        this.dialogRef.afterClosed().subscribe((response) => {
            if (!response?.period) {
                return;
            }

            const basePeriods = this.hasPeriods ? (this.project.periods ?? []) : [this.buildDefaultDayPeriod()];
            this.persistPeriods(replaceProjectPeriodCoverage(basePeriods, response.period));
        });
    }

    private openPeriodPanel(data: any) {
        return this.matDialog.open(ProjectPeriodAddEditComponent, {
            data,
            panelClass: "right-panel-dialog",
            width: "50vw",
            maxWidth: "100vw",
            height: "100%",
            maxHeight: "100%",
            position: { right: "0", top: "0", bottom: "0" },
            hasBackdrop: true,
            disableClose: false,
        });
    }

    private getNormalizedPeriods(periods: ProjectPeriodModel[]): ProjectPeriodModel[] {
        return periods.map((period) => normalizeProjectPeriod(period));
    }

    private getUpsertedPeriods(period: ProjectPeriodModel, previousName?: string) {
        const nextPeriod = normalizeProjectPeriod(period);
        const existingIndex = (this.project.periods ?? []).findIndex((candidate) => candidate.name === (previousName ?? nextPeriod.name));
        if (existingIndex >= 0) {
            const nextPeriods = [...(this.project.periods ?? [])];
            nextPeriods[existingIndex] = nextPeriod;
            return nextPeriods;
        }

        return [...(this.project.periods ?? []), nextPeriod];
    }

    private toTimelineSegments(period: ProjectPeriodModel, toneIndex: number, isVirtual: boolean): TimelineSegment[] {
        return getProjectPeriodSegments(period).map((segment, segmentIndex) => ({
            key: `${period.name}-${segment.start}-${segment.end}-${segmentIndex}`,
            period,
            segmentStart: segment.start,
            segmentEnd: segment.end,
            left: (segment.start / (24 * 60)) * 100,
            width: ((segment.end - segment.start) / (24 * 60)) * 100,
            canSplit: segment.end - segment.start >= 60,
            toneIndex,
            isVirtual,
        }));
    }

    private getSplitMinute(segment: TimelineSegment, event: MouseEvent): number | null {
        const timelineMinute = this.getTimelineMinute(event);
        if (timelineMinute === null) {
            return null;
        }

        return this.clampSplitMinute(segment, timelineMinute);
    }

    private getTimelineMinute(event: MouseEvent): number | null {
        const target = event.currentTarget as HTMLElement | null;
        const track = target?.closest(".timeline-track") as HTMLElement | null;
        if (!track) {
            return null;
        }

        const rect = track.getBoundingClientRect();
        if (!rect.width) {
            return null;
        }

        const relativeX = Math.min(rect.width, Math.max(0, event.clientX - rect.left));
        const rawSplit = (relativeX / rect.width) * 24 * 60;
        return Math.round(rawSplit / 30) * 30;
    }

    private clampSplitMinute(segment: TimelineSegment, minute: number): number | null {
        if (!segment.canSplit) {
            return null;
        }

        return Math.min(segment.segmentEnd - 30, Math.max(segment.segmentStart + 30, minute));
    }

    private buildSplitPeriod(segment: TimelineSegment, splitAt: number): ProjectPeriodModel {
        return {
            name: this.buildSplitName(segment.period.name),
            intervals: [
                {
                    startTime: this.minutesToTime(splitAt),
                    endTime: this.minutesToTime(segment.segmentEnd % (24 * 60)),
                },
            ],
        };
    }

    private buildDefaultDayPeriod(): ProjectPeriodModel {
        return {
            name: "Day",
            intervals: [
                {
                    startTime: { hour: 0, minute: 0 },
                    endTime: { hour: 0, minute: 0 },
                },
            ],
        };
    }

    private buildSplitName(baseName: string): string {
        const periods = this.project?.periods ?? [];
        let suffix = 2;
        let candidate = `${baseName} ${suffix}`;
        const existingNames = new Set(periods.map((period) => period.name));

        while (existingNames.has(candidate)) {
            suffix += 1;
            candidate = `${baseName} ${suffix}`;
        }

        return candidate;
    }

    private minutesToTime(totalMinutes: number) {
        const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
        return {
            hour: Math.floor(normalized / 60),
            minute: normalized % 60,
        };
    }

    private formatMinute(totalMinutes: number): string {
        const normalized = ((totalMinutes % (24 * 60)) + 24 * 60) % (24 * 60);
        const hours = Math.floor(normalized / 60)
            .toString()
            .padStart(2, "0");
        const minutes = (normalized % 60).toString().padStart(2, "0");
        return `${hours}:${minutes}`;
    }

    private formatSegmentEnd(segment: TimelineSegment): string {
        return this.formatSegmentBoundary(segment.segmentStart, segment.segmentEnd);
    }

    private formatSegmentBoundary(startMinute: number, endMinute: number): string {
        if (startMinute > 0 && endMinute === 24 * 60) {
            return "24:00";
        }

        return this.formatMinute(endMinute);
    }
}
